export interface User {
  id: number;
  username: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  phoneNumber?: string;
  emergencyContactName?: string;
  emergencyContactNumber?: string;
  premiumActivatedAt?: Date;
  premiumExpiresAt?: Date;
  isPremium?: boolean;
  createdAt: Date;
  updatedAt: Date;
  photo?: string;
  hikeCount?: number;
  totalHikeDistance?: number;
  savedTrails?: number[];
  fcmTokens?: string[];
  onboardingAnswers?: { question: string; answer: string }[];
}

export enum UserRole {
  Admin = 'admin',
  Hiker = 'hiker'
}

export enum UserStatus {
  Active = 'active',
  Deleted = 'deleted',
  Inactive = 'inactive',
  Pending = 'pending'
}

export interface UserStats {
  totalUsers: number;
  guestUsers: number;
  signedUpUsers: number;
  usersByStatus: {
    active: number;
    pending: number;
    inactive: number;
    deleted: number;
  };
  usersByRole: {
    admin: number;
    hiker: number;
  };
  premiumUsers: number;
  recentRegistrations: number;
  period: string;
}

export interface UsersResponse {
  users: User[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export interface UserFilters {
  name?: string;
  email?: string;
  phoneNumber?: string;
  status?: UserStatus;
  role?: UserRole;
  userType?: 'guest' | 'signedUp';
  isPremium?: string;
  page?: number;
  pageSize?: number;
}

export const getUserStatusBadgeVariant = (status: UserStatus): string => {
  switch (status) {
    case UserStatus.Active:
      return 'success';
    case UserStatus.Pending:
      return 'warning';
    case UserStatus.Inactive:
      return 'secondary';
    case UserStatus.Deleted:
      return 'danger';
    default:
      return 'secondary';
  }
};

export const getUserRoleBadgeVariant = (role: UserRole): string => {
  switch (role) {
    case UserRole.Admin:
      return 'primary';
    case UserRole.Hiker:
      return 'info';
    default:
      return 'secondary';
  }
};

export const formatUserType = (email: string): string => {
  return email.includes('guest') ? 'Guest' : 'Registered';
};

export const formatPremiumStatus = (user: User): string => {
  // Calculate isPremium based on premiumExpiresAt > current date
  const isPremium = user.premiumExpiresAt ? new Date(user.premiumExpiresAt) > new Date() : false;
  return isPremium ? 'Premium' : 'Standard';
};

export const isUserPremium = (user: User): boolean => {
  // Calculate if user has active premium based on premiumExpiresAt
  return user.premiumExpiresAt ? new Date(user.premiumExpiresAt) > new Date() : false;
};

export const truncateText = (text: string, maxLength: number = 30): string => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};